import { useState } from "react";
import { vLabel } from "@/lib/version";
import { toast } from "sonner";
import { Eye, EyeOff, FileText, Trash2 } from "lucide-react";
import { api } from "@/lib/api";
import { useI18n } from "@/i18n/I18nContext";
import { translateError } from "@/i18n/errors";
import { useSpace } from "@/context/SpaceContext";
import { ConfirmButton } from "@/components/admin/ConfirmButton";

const VersionRow = ({ v, prev, active, onChanged, onEditDescription }) => {
  const { t, fmtBytes, fmtDate, fmtDownloads } = useI18n();
  const [busy, setBusy] = useState(false);
  const { base } = useSpace();
  const act = async (fn, msg) => {
    setBusy(true);
    try {
      await fn();
      toast.success(t(msg, { v: vLabel(v) }));
      onChanged();
    } catch (err) {
      toast.error(translateError(err, t));
    } finally {
      setBusy(false);
    }
  };
  return (
    <li className={`flex flex-col gap-3 rounded-xl border p-4 sm:flex-row sm:items-center ${v.hidden ? "border-neutral-800 bg-black/40 opacity-70" : "border-[rgba(245,197,66,0.25)] bg-black/30"}`} data-testid={`version-row-${v.version}`}>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-display text-sm font-bold text-[var(--gold-bright)]" data-testid="version-number">{vLabel(v)}</span>
          {active && <span className="rounded-full bg-[var(--gold)] px-2 py-0.5 text-[0.65rem] font-bold uppercase text-black" data-testid="version-active-badge">{t("versions.active")}</span>}
          {v.hidden && <span className="rounded-full border border-neutral-600 px-2 py-0.5 text-[0.65rem] uppercase text-neutral-400" data-testid="version-hidden-badge">{t("versions.hidden")}</span>}
        </div>
        <p className="mt-1 break-all text-xs text-neutral-400">{v.filename} · {fmtBytes(v.size)} · {fmtDate(v.created_at)} · <span className="text-[var(--gold-bright)]" data-testid={`version-downloads-${v.id}`}>{fmtDownloads(v.downloads)}</span></p>
        <Fingerprint v={v} prev={prev} />
        <div className="mt-1 flex flex-wrap items-center gap-2 text-xs">
          {v.tech_status === "ok" && v.manifest ? (
            <span className="break-all text-neutral-500" data-testid={`version-tech-${v.version}`}>{v.manifest.package}{v.manifest.min_android ? ` · Android ≥ ${v.manifest.min_android}` : ""}
              <CopyBtn value={v.manifest.package} testId={`copy-package-${v.version}`} /></span>
          ) : (
            <span className={v.tech_status === "pending" ? "text-neutral-500" : "font-semibold text-red-300"} data-testid={`version-tech-${v.version}`}>
              {t(v.tech_status === "pending" ? "tech.pending" : "tech.unavailable")}
            </span>
          )}
          <span className={v.has_description ? "text-emerald-300" : "text-neutral-500"} data-testid={`version-desc-status-${v.version}`}>
            · {v.has_description ? t("desc.has", { n: v.translations_ok }) : t("desc.none")}
          </span>
        </div>
      </div>
      <div className="flex flex-wrap gap-2">
        <button type="button" className="btn-gold btn-sm" onClick={() => onEditDescription(v)} data-testid={`version-desc-btn-${v.version}`}>
          <FileText className="h-4 w-4" /> {t("desc.button")}
        </button>
        {v.hidden ? (
          <button type="button" className="btn-gold btn-sm" disabled={busy} onClick={() => act(() => api.post(`${base}/versions/${v.id}/show`), "versions.shownToast")} data-testid={`version-show-btn-${v.version}`}>
            <Eye className="h-4 w-4" /> {t("versions.show")}
          </button>
        ) : (
          <button type="button" className="btn-red btn-sm" disabled={busy} onClick={() => act(() => api.post(`${base}/versions/${v.id}/hide`), "versions.hiddenToast")} data-testid={`version-hide-btn-${v.version}`}>
            <EyeOff className="h-4 w-4" /> {t("versions.hide")}
          </button>
        )}
        <ConfirmButton
          title={t("versions.deleteTitle", { v: vLabel(v) })}
          description={t("versions.deleteDesc")}
          onConfirm={() => act(() => api.delete(`${base}/versions/${v.id}`), "versions.deletedToast")}
          className="btn-red btn-sm"
          testId={`version-delete-btn-${v.version}`}
        >
          <Trash2 className="h-4 w-4" /> {t("versions.delete")}
        </ConfirmButton>
      </div>
    </li>
  );
};


const CopyBtn = ({ value, testId }) => {
  const { t } = useI18n();
  return (
    <button type="button" className="ms-2 rounded border border-[rgba(245,197,66,0.4)] px-1.5 text-[0.65rem] text-[var(--gold)]" data-testid={testId}
      onClick={() => navigator.clipboard?.writeText(value).then(() => toast.success(t("verify.copied")))}>{t("verify.copy")}</button>
  );
};

const Fingerprint = ({ v, prev }) => {
  const { t } = useI18n();
  const fp = v.signing_sha256;
  const changed = fp && prev?.signing_sha256 && prev.signing_sha256 !== fp;
  return (
    <div className="mt-1 min-w-0 text-[0.7rem]" data-testid={`version-cert-${v.version}`}>
      <span className="text-neutral-500">{t("verify.fingerprint")} : </span>
      {fp ? <><span dir="ltr" className="break-all font-mono text-neutral-300">{fp}</span><CopyBtn value={fp} testId={`copy-cert-${v.version}`} /></>
        : <span className="text-neutral-500">{t("verify.unavailable")}</span>}
      {changed && <p className="mt-1 font-semibold text-amber-300" role="alert" data-testid={`version-cert-changed-${v.version}`}>{t("verify.certChanged")}</p>}
    </div>
  );
};

export const VersionList = ({ versions, onChanged, onEditDescription }) => {
  const { t } = useI18n();
  const activeId = versions.find((v) => !v.hidden)?.id;
  return (
    <section className="card-carbon rounded-2xl p-6" data-testid="version-list">
      <h2 className="font-display text-sm font-bold uppercase tracking-wider text-white">
        {t("versions.title")} <span className="text-[var(--gold)]">({versions.length}/5)</span>
      </h2>
      <p className="mt-2 text-xs text-neutral-400">{t("versions.hint")}</p>
      {versions.length === 0 ? (
        <p className="mt-5 text-sm text-neutral-400" data-testid="versions-empty">{t("apps.noVersion")}</p>
      ) : (
        <ul className="mt-5 space-y-3">
          {versions.map((v, i) => <VersionRow key={v.id} v={v} prev={versions[i + 1]} active={v.id === activeId} onChanged={onChanged} onEditDescription={onEditDescription} />)}
        </ul>
      )}
    </section>
  );
};
